import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, model, output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {SvgIconComponent} from 'angular-svg-icon';
import {UpgraderNewService} from '../../services/upgrader-new.service';
import {SessionService} from '@core/services/session.service';
import {ModalService} from '@features/modals/services/modal.service';
import {SocketService} from '@core/services/socket.service';
import {Skin} from '@core/interfaces/skin.interface';
import {UpgraderSkin} from '../../interfaces/upgrader-skin.interface';
import {SearchInputComponent} from '@core/components/search-input/search-input.component';
import {CheckboxComponent} from '@core/components/checkbox/checkbox.component';
import {ToggleComponent} from '@core/components/toggle/toggle.component';
import {SkinsListComponent} from '@core/components/skins-list/skins-list.component';
import {LoadingIconComponent} from '@core/components/loading-icon/loading-icon.component';
import {TablePaginationComponent} from '@core/components/table/table-pagination/table-pagination.component';
import {fadeInAnimation, rotateRefreshAnimation} from '@core/utils/animations.util';
import {createUpgraderGridBreakpoint} from '../../upgrader-grid.util';
import {ToastrService} from 'ngx-toastr';
import {getNextSortOption, SORT_OPTIONS, SortOption} from '../../upgrader.constants';
import {fromEvent} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-upgrader-inventory',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    SearchInputComponent,
    CheckboxComponent,
    ToggleComponent,
    SkinsListComponent,
    LoadingIconComponent,
    TablePaginationComponent,
  ],
  templateUrl: './upgrader-inventory.component.html',
  styleUrl: './upgrader-inventory.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInAnimation, rotateRefreshAnimation],
})
export class UpgraderInventoryComponent {
  readonly upgraderService = inject(UpgraderNewService);
  readonly sessionService = inject(SessionService);
  private readonly modalService = inject(ModalService);
  private readonly socketService = inject(SocketService);
  private readonly toastrService = inject(ToastrService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly gridBreakpoint = createUpgraderGridBreakpoint(this.destroyRef);
  readonly gridConfig  = computed(() => ({ columns: this.gridBreakpoint().columns }));
  private readonly PAGE_SIZE = computed(() => this.gridBreakpoint().pageSize);

  interacted = output<void>();

  // Local state
  readonly search = signal('');
  readonly showPureSkins = signal(false);
  readonly isLoading = signal(false);
  readonly isRefreshing = signal(false);
  readonly isWithdrawing = signal(false);
  readonly isSelling = signal(false);
  readonly inventorySkins = signal<UpgraderSkin[]>([]);
  readonly selectedSkins = model<UpgraderSkin[]>([]);
  readonly error = signal<string | null>(null);

  readonly sortOptions = SORT_OPTIONS;
  readonly currentSort = signal<SortOption>(SORT_OPTIONS[0]);

  // Filtered and sorted skins
  readonly displayedSkins = computed(() => {
    let skins = this.inventorySkins();

    // Filter by search
    const searchTerm = this.search().toLowerCase();
    if (searchTerm) {
      skins = skins.filter(s =>
        s.name.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by pure skins if enabled
    if (this.showPureSkins()) {
      const pureSkinNames = this.sessionService.pureSkins();
      skins = skins.filter(s => pureSkinNames.includes(s.name));
    }

    // Sort
    const sortKey = this.currentSort().key;
    skins = [...skins].sort((a, b) => {
      if (sortKey === 'highest_price') {
        return b.price - a.price;
      }
      return a.price - b.price;
    });

    return skins;
  });

  // Pagination
  readonly pageIndex = signal(0);
  readonly totalPages = computed(() => Math.ceil(this.displayedSkins().length / this.PAGE_SIZE()));
  readonly paginatedSkins = computed(() => {
    const size = this.PAGE_SIZE();
    const start = this.pageIndex() * size;
    return this.displayedSkins().slice(start, start + size);
  });

  constructor() {
    // Reset page when filters change
    effect(() => {
      this.search();
      this.currentSort();
      this.showPureSkins();
      this.pageIndex.set(0);
    });

    // Load on init and on every reconnect.
    // loadInventory() has a 4-second timeout + up to 2 retries, so it handles
    // the case where the server hasn't finished authenticating yet (page reload).
    fromEvent(this.socketService.socket, 'connect')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadInventory());

    if (this.socketService.socket.connected) {
      this.loadInventory();
    }
  }

  async loadInventory(attempt = 0): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const skins = await this.upgraderService.loadDepositSkins();
      this.inventorySkins.set(skins);
    } catch {
      // Server not ready yet (timeout) — retry up to 2 more times
      if (attempt < 2) {
        return this.loadInventory(attempt + 1);
      }
      this.error.set('Failed to load inventory');
    } finally {
      this.isLoading.set(false);
    }
  }

  async refreshInventory() {
    this.isRefreshing.set(true);
    this.search.set('');
    this.selectedSkins.set([]);
    await this.loadInventory();
    this.isRefreshing.set(false);
  }

  onDeposit() {
    this.interacted.emit();
    this.modalService.openModal('UpgraderDepositModal', {
      type: this.sessionService.balanceType(),
    }).subscribe();
  }

  async onWithdraw() {
    if (!this.selectedSkins().length || this.isWithdrawing()) return;
    this.isWithdrawing.set(true);
    try {
      const message = await this.socketService.emit('inventory_withdraw', {
        skins: this.selectedSkins().map(s => s.id),
        balance: this.sessionService.balanceType(),
      });
      this.toastrService.success(message);
      this.selectedSkins.set([]);
    } catch {
    } finally {
      this.isWithdrawing.set(false);
    }
  }

  async onSell() {
    if (!this.selectedSkins().length || this.isSelling()) return;
    this.isSelling.set(true);
    try {
      const message = await this.socketService.emit('inventory_sell', {
        skins: this.selectedSkins().map(s => s.id),
        balance: this.sessionService.balanceType(),
      });
      this.toastrService.success(message);
      this.selectedSkins.set([]);
    } catch {
    } finally {
      this.isSelling.set(false);
    }
  }

  toggleSort() {
    this.currentSort.set(getNextSortOption(this.currentSort()));
  }

  onSearchChange(value: string) {
    this.interacted.emit();
    this.search.set(value);
  }

  onPureToggle(value: boolean) {
    this.interacted.emit();
    this.showPureSkins.set(value);
  }

  nextPage() { this.pageIndex.update(i => Math.min(i + 1, this.totalPages() - 1)); }
  prevPage() { this.pageIndex.update(i => Math.max(i - 1, 0)); }
}
